// Who has to be credited on the map, and what the credit has to say.
//
// WHY THIS EXISTS
//
// The map redistributes twenty-four third-party geospatial sources and, until
// this file, it credited none of them anywhere a farmer could see. What looked
// like attribution was not:
//
//   * `legend-metadata.js` carries an `accordionSummary` for eleven entries,
//     each ending in a "Data: <publisher>" sentence. NOTHING renders that field
//     (see lib/useLegend.js and MapLayerPanel.js), so those eleven credits were
//     in the bundle and invisible. A licence register written on 07/09/2026
//     recorded the legend as crediting eleven of twenty-four sources; that was
//     counting these strings, and it is wrong. The real figure was zero.
//   * `GradedLayerRow.js` renders "Based on <source>" in the Zone Details
//     panel, from SOURCE_NOTES in aggregateLandSummary.js. That names six
//     products for a farmer's benefit. It is not a licence credit, it only
//     appears once a field is open, and it covers six of the twenty-four.
//   * The ONS and OS boundary attribution is written out correctly in the
//     headers of lib/walesBoundary.js and lib/scotlandBoundary.js, as a
//     comment, and has never reached a screen.
//
// WHAT THIS FILE IS, AND WHAT IT IS NOT
//
// It is a register of attribution OBLIGATIONS and a completeness gate: every
// source in the catalogue must appear here, so a new layer cannot ship
// uncredited without a test failing.
//
// It is NOT a licence determination and it is NOT legal advice. Nothing here
// interprets a licence. `attributionText` is only ever a string somebody has
// actually read off the publisher's own terms; where nobody has, it is `null`
// and the entry says so. Clearing the Hutton commercial clause and the
// Cranfield LandIS agreement are conversations, not code, and are tracked
// outside this repo.

/**
 * Required wherever the app is used, not tied to a layer being switched on.
 *
 * The ONS country boundaries are loaded on every map: `classifyPoint` in
 * lib/useViewportRegion.js arbitrates the England/Wales and England/Scotland
 * bounding-box overlaps with them, so the data is in use whether or not any
 * overlay is visible. Verbatim from the ONS licensing page cited in the two
 * boundary modules' headers; attributions.test.js asserts this string and those
 * comments cannot drift apart.
 */
export const ONS_OS_BOUNDARY_ATTRIBUTION =
  'Contains OS data (c) Crown copyright and database right 2024. Contains ONS ' +
  'data (c) Crown copyright and database right 2024. Source: Office for ' +
  'National Statistics licensed under the Open Government Licence v3.0.';

export const ALWAYS_REQUIRED = Object.freeze([
  Object.freeze({
    id: 'ons-os-country-boundaries',
    licensor: 'Office for National Statistics; Ordnance Survey',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.ons.gov.uk/methodology/geography/licences',
    attributionRequired: true,
    attributionText: ONS_OS_BOUNDARY_ATTRIBUTION,
  }),
]);

// `attributionText: null` means NOBODY HAS READ THE REQUIRED WORDING OFF THE
// PUBLISHER'S TERMS, not that no attribution is required. `attributionRequired`
// says whether one is owed. The pair (true, null) is the outstanding case, and
// attributions.test.js pins exactly which sources are in it, so the list can
// only shrink deliberately.
//
// `attributionText` may be a STRING or an ARRAY of strings. NatureScot specify
// three separate credits, one per designation, and they differ in how the body
// names itself ("SNH", "NatureScot", "NatureScot (Scottish Natural Heritage)").
// Collapsing them into one line would mean composing wording rather than
// quoting it, which is the one thing this register must not do, so the shape
// takes a list instead.
//
// `attributionUrl` is for the case where the publisher's required statement is
// a DOCUMENT rather than a line. OGL v3 requires the licensee to "acknowledge
// the source of the Information in your product or application by including or
// linking to any attribution statement specified by the Information
// Provider(s)". SEPA's flood products need roughly forty third-party
// statements from around thirty organisations, which cannot go in a legend
// panel, so linking is the route the licence itself offers.
//
// `licensor` is taken verbatim from the publisher named in that source's own
// catalogue entry or its legend-metadata `accordionSummary`. It records who the
// app already says the data came from. It is not a finding about who holds the
// rights.
const outstanding = (licensor, note, reference = null) =>
  Object.freeze({
    licensor,
    licence: null,
    reference,
    attributionRequired: true,
    attributionText: null,
    note,
  });

// Verbatim from SEPA's own attribution PDF, which is the document their licence
// condition points at.
const SEPA_ATTRIBUTION =
  '\u00a9SEPA 2025; this SEPA product is licenced under the Open Government ' +
  'Licence 3.0.';

// The roughly forty third-party statements SEPA additionally require. Linked
// rather than rendered, per OGL v3's "including or linking to".
const SEPA_ATTRIBUTION_DOCUMENT =
  'https://www.sepa.org.uk/media/5zidnja5/sepa_flood_maps_data_attribution_statements.pdf';

// RESEARCHED 10/09/2026. Every England and Wales source turned out to be OGL,
// declared per-dataset by the publisher on the endpoint the app actually calls,
// with no fee and no access constraint. These are the credits those publishers
// specify, verbatim.

// The OGL v3 fallback, for a source that is OGL and specifies no wording of its
// own. Quoted from the licence itself.
const OGL_DEFAULT_ATTRIBUTION =
  'Contains public sector information licensed under the Open Government ' +
  'Licence v3.0.';

// Natural England use one credit across SSSI, SAC, SPA and ALC. On ALC they
// ship a literal `[year]` placeholder, which is why that entry stays
// outstanding while the designations do not.
const NATURAL_ENGLAND_ATTRIBUTION =
  '\u00a9 Natural England copyright. Contains Ordnance Survey data \u00a9 Crown ' +
  'copyright and database right 2024.';

// Natural Resources Wales specify this for SSSI, SAC and SPA. The OS licence
// number in it is NRW's OWN, not one BBB needs: OS's guidance is that the
// member inserts THEIR licence number, and BBB is not a PSGA member. This is
// where AC0000849444 actually lives, which the June 2026 handover recorded as
// a string the app had and did not render. The app never had it.
const NRW_DESIGNATIONS_ATTRIBUTION =
  'Contains Natural Resources Wales information \u00a9 Natural Resources Wales ' +
  'and Database Right. All rights Reserved. Contains Ordnance Survey Data. ' +
  'Ordnance Survey Licence number AC0000849444. Crown Copyright and Database ' +
  'Right.';

// The Welsh flood layer group's own credit. Note it names Cranfield and Hutton:
// four England and Wales layers name them, not just the Scottish soil sources.
// Note also the numberless OS form here against the numbered form on the
// designations above. That difference is NRW's and is deliberate; whether it
// signifies an OS derived-data distinction is the one thing on the Welsh side
// worth a solicitor.
const NRW_FLOOD_ATTRIBUTION =
  'Contains Natural Resources Wales information \u00a9 Natural Resources Wales ' +
  'and database right. All rights reserved. Some features of this information ' +
  'are based on digital spatial data licensed from the UK Centre for Ecology ' +
  '& Hydrology \u00a9 UKCEH. Defra, Met Office and DARD Rivers Agency \u00a9 Crown ' +
  'copyright. \u00a9 Cranfield University. \u00a9 James Hutton Institute. Contains ' +
  'OS data \u00a9 Crown copyright and database right.';

export const SOURCE_ATTRIBUTIONS = Object.freeze({
  // --- Soil ---------------------------------------------------------------
  // Cranfield is credited nowhere as a licensor today, only as a product name:
  // "Soilscapes at 1:250,000" in SOURCE_NOTES.
  //
  // RESEARCHED 10/09/2026. The terms are not the blocker; the access route is.
  // Cranfield's LandIS Soil Portal EULA of 01/03/2026 lets a Public User
  // "exploit the Public Data commercially and non-commercially", royalty-free,
  // with a specified credit. But that EULA attaches to the account-gated
  // Portal, and the two endpoints this app calls are anonymous ArcGIS services
  // on the same host, outside that flow. The only licence statement naming one
  // of them says "Non-commercial use only by Catchment-Based Approach
  // Partnerships", and it was published by the Rivers Trust, not Cranfield.
  //
  // Unblocking is one email to landis@cranfield.ac.uk asking whether those two
  // anonymous services count as Public Data on the Platform. Not a solicitor.
  // There is NO OGL national soil map of England and Wales, so this layer has
  // no substitute: dropping it loses "what is my soil" for most of the farmer
  // base and leaves only land grade, which is a different question.
  'soilscapes-england': outstanding(
    'Cranfield University (LandIS / Soilscapes)',
    'Terms would permit this: the 01/03/2026 LandIS Portal EULA allows commercial exploitation of Public Data, royalty-free, with a specified credit. Blocked on the access route, because the app consumes anonymous services outside that Portal flow. One email to landis@cranfield.ac.uk closes it.',
    'https://www.landis.org.uk/'
  ),
  'soilscapes-wales': outstanding(
    'Cranfield University (LandIS / Soilscapes)',
    'Terms would permit this: the 01/03/2026 LandIS Portal EULA allows commercial exploitation of Public Data, royalty-free, with a specified credit. Blocked on the access route, because the app consumes anonymous services outside that Portal flow. One email to landis@cranfield.ac.uk closes it.',
    'https://www.landis.org.uk/'
  ),
  // SETTLED 10/09/2026, and this is the finding that unblocked the row that had
  // been open since 18/08. The James Hutton Institute Open Data Licence is real
  // and it is not on a web page: it ships inside the data downloads, byte
  // identical across the three relevant datasets. Clause 1.1 grants a
  // "worldwide, royalty-free, non-exclusive licence to use the Data for any
  // purpose, including both commercial and non-commercial uses", clause 1.2
  // free of charge. Hutton's own 250k metadata adds "This data may be used for
  // commercial purposes under the terms of the Hutton Open Data licence", and
  // the Scottish Government's own spatial data catalogue names the same licence
  // with "no limitations on public access".
  //
  // NOT via soils.environment.gov.scot, which was the hypothesis. That portal's
  // own terms are NON-COMMERCIAL by default, so the earlier `reference` URLs
  // here pointed at the wrong publisher's terms and at more restrictive ones.
  // Corrected below.
  //
  // The wording is clause 4.5 verbatim. Two things deliberately left as
  // published rather than tidied: it says Open Government Licence v.2.0, not
  // v3.0, and that is what the document says; and the square brackets are
  // Hutton's own placeholders, filled here from Hutton's own metadata rather
  // than invented. See the note field for exactly which values and from where.
  //
  // Clause 4.2 is a separate live obligation and is NOT an attribution matter:
  // it asks to be told by email about any Derived Data. One email to
  // soils@hutton.ac.uk describing the app discharges it.
  'hutton-soil-250k-scotland': Object.freeze({
    licensor: 'The James Hutton Institute',
    licence: 'The James Hutton Institute Open Data Licence',
    reference: 'https://spatialdata.gov.scot/geonetwork/srv/api/records/B7E65842-C041-4950-BF0C-3AF06C2DBAE7',
    attributionRequired: true,
    attributionText:
      'Soil Maps of Scotland at a scale of 1:250 000 copyright and database ' +
      'right The James Hutton Institute 2020. Used with the permission of The ' +
      'James Hutton Institute. All rights reserved. Any public sector ' +
      'information contained in these data is licensed under the Open ' +
      'Government Licence v.2.0.',
    note:
      "Clause 4.5 verbatim. Placeholders filled from Hutton's own words: the " +
      'dataset name from the citation in _ReadMe_.txt inside ' +
      'Hutton_Soils_250K_v1.4.zip, and 2020 from that file\'s own date of 18 ' +
      'November 2020. CONFIRM the revision year with Hutton if it matters; it ' +
      'is the one judgement call in this entry.',
  }),
  'hutton-soil-25k-scotland': Object.freeze({
    licensor: 'The James Hutton Institute',
    licence: 'The James Hutton Institute Open Data Licence',
    reference: 'https://doi.org/10.5281/zenodo.5159133',
    attributionRequired: true,
    attributionText:
      'Soil maps of Scotland (partial coverage) at a scale of 1:25 000 ' +
      'copyright and database right The James Hutton Institute 1987. Used ' +
      'with the permission of The James Hutton Institute. All rights ' +
      'reserved. Any public sector information contained in these data is ' +
      'licensed under the Open Government Licence v.2.0.',
    note:
      'Clause 4.5 verbatim, same licence as the 250k source. Dataset name and ' +
      'the 1970 to 1987 survey range from the Zenodo citation for record ' +
      '5159133; 1987 is the end of that range. This endpoint is separately ' +
      'known to be DOWN (the druid.hutton.ac.uk host outage), so nothing ' +
      'renders from it today either way.',
  }),

  // --- Land capability ----------------------------------------------------
  // ONE PLACEHOLDER AWAY, 10/09/2026. OGL v3 named by URL in the item's
  // `licenseInfo`, no access constraint. But Natural England ship a LITERAL
  // `[year]` in this credit, in `copyrightText`, `accessInformation` and the
  // dataset's own attribution sentence alike, so there is nothing to quote
  // without choosing a year. Choosing one is inventing wording, so this stays
  // outstanding on a one-question blocker rather than shipping a guess.
  //
  // Their designations credit (above) resolves to 2024, which is the obvious
  // candidate, but obvious is not the same as stated.
  'alc-provisional-england': outstanding(
    'Natural England',
    'Licence settled: OGL v3 named by URL in the service item, no access constraint. The credit is published as "(c) Natural England copyright. Contains Ordnance Survey data (c) Crown copyright and database right [year]." with a LITERAL [year] placeholder in every field it appears in. One question, not a research job: which year. Their designations credit says 2024.',
    'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/'
  ),
  // SETTLED 10/09/2026, on the OGL default. OGL v3 is declared twice, in the
  // DataMapWales catalogue record and again in the Welsh Government's own
  // guidance note. The publisher specifies NO wording of its own, and OGL v3's
  // requirement is to include "any attribution statement specified by the
  // Information Provider(s)", so where none is specified the licence's own
  // default form of words is what it asks for. Using it is quoting the licence,
  // not composing a credit.
  //
  // Two things that are not attribution. The dataset carries a Cranfield IP
  // reservation worth a legal read, and it carries strong fitness caveats
  // including that it "is not intended to replace the need for Agricultural
  // Land Classification survey work", plus a list of uses the Welsh Government
  // "does not approve of". Those shape the layer's farmer-facing wording, and
  // the legal read is not mine.
  'alc2-predictive-wales': Object.freeze({
    licensor: 'Welsh Government; Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.gov.wales/agricultural-land-classification',
    attributionRequired: true,
    attributionText: OGL_DEFAULT_ATTRIBUTION,
    note:
      'The publisher specifies no wording, so this is OGL v3\'s own default ' +
      'statement, quoted from the licence. Separately: a Cranfield IP ' +
      'reservation on this dataset wants a legal read, and its fitness ' +
      'caveats want to reach the farmer-facing copy.',
  }),
  // STILL BLOCKED 10/09/2026, and the reason changed. It is not "nobody has
  // read the terms" any more: Hutton say two different things and the more
  // restrictive one is attached to the live service. Their Open Data Licence
  // permits commercial use; their own ArcGIS item for the LCA service says "May
  // not be used for commercial gain", and that note sits on the exact endpoint
  // the Zone Details panel queries. The Scottish Government's own Prime Land
  // record names the Open Data Licence too, which makes it three publisher
  // statements to one, but three to one is not a resolution.
  //
  // Only Hutton can resolve it, and they are a research partner, so asking is
  // cheap. Prime Land is a coarser fallback on a MapServer the app already
  // calls, if the answer is no.
  'hutton-lca-scotland': outstanding(
    'The James Hutton Institute',
    'CONFLICT inside one publisher, not an unread licence. Their Open Data Licence permits commercial use; their own ArcGIS item for this service says "May not be used for commercial gain", on the exact endpoint the panel queries. Needs Hutton to say which governs. Clause 4.5 gives the wording once it is resolved.',
    'https://doi.org/10.5281/zenodo.6322683'
  ),

  // --- Flood --------------------------------------------------------------
  'england-flood-risk-rivers-sea': Object.freeze({
    licensor: 'Environment Agency',
    licence: 'Open Government Licence',
    reference: 'https://www.data.gov.uk/',
    attributionRequired: true,
    attributionText:
      '\u00a9 Environment Agency copyright and/or database right 2025. All rights reserved.',
    note:
      "Verbatim from the dataset's own \"Attribution statement:\" sentence on " +
      'data.gov.uk. The service itself reports Fees: NONE and no use ' +
      'constraint. The 2025 is the publisher\'s.',
  }),
  'ea-surface-water-england': Object.freeze({
    licensor: 'Environment Agency',
    licence: 'Open Government Licence',
    reference: 'https://www.data.gov.uk/',
    attributionRequired: true,
    attributionText:
      '\u00a9 Environment Agency copyright and/or database right 2025. All rights reserved.',
    note: 'Same publisher and same specified credit as the rivers and sea layer.',
  }),
  // SETTLED 10/09/2026. OGL v3 per dataset, AccessConstraints: NONE, and one
  // credit for the whole flood layer group.
  //
  // TWO things about this group that are NOT attribution and are recorded here
  // only so they are not lost. First, its credit names Cranfield and Hutton, so
  // four England and Wales layers name them and not just the Scottish soil
  // sources. Second, NRW state a maximum display scale of 1:5,000 for this
  // data, and the app enforces no maximum zoom on these layers, so it exceeds
  // that today. That is a live defect rather than a paperwork gap and it
  // belongs on BBB-418, not here.
  'wales-flood-risk-rivers': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_FLOOD_ATTRIBUTION,
    note:
      "Verbatim from the flood layer group's own \"Attribution Statement\" " +
      'section in the DataMapWales catalogue. Note the numberless OS form ' +
      'here against the numbered form NRW use on the designations; that ' +
      'difference is theirs and is the one Welsh question worth a solicitor.',
  }),
  'wales-flood-risk-sea': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_FLOOD_ATTRIBUTION,
    note: 'Same layer group and same specified credit as the rivers layer.',
  }),
  'wales-flood-risk-surface-water': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_FLOOD_ATTRIBUTION,
    note: 'Same layer group and same specified credit as the rivers layer.',
  }),
  // SETTLED 10/09/2026 as to the licence, and handled by LINKING as to the
  // wording. SEPA's `copyrightText` on the exact service this app calls states
  // OGL v3, and nothing in their terms distinguishes commercial from
  // non-commercial.
  //
  // The wording is the awkward part and the awkwardness is SEPA's, not ours.
  // Their `Flood Map Version 3.0 Data Attribution Statements`, 9 pages dated
  // 05/02/2025, opens: "The following attribution statements must be included
  // when reusing the SEPA Products in addition to the following SEPA required
  // attribution statement." It then lists roughly forty statements from around
  // thirty organisations, and this app draws a layer from all three of its
  // sections. Forty statements cannot go in a legend panel.
  //
  // OGL v3 itself provides the way out: it requires the licensee to
  // "acknowledge the source of the Information in your product or application
  // by including or linking to any attribution statement specified by the
  // Information Provider(s)". So the SEPA line renders and the document is
  // linked. That is the licence's own alternative, not a shortcut round it.
  //
  // "licenced" is SEPA's spelling in the PDF the licence condition points at.
  // Their `copyrightText` field spells it "licensed". Same statement, their own
  // inconsistency; the PDF's spelling is used because the PDF is the document.
  'sepa-flood-rivers-sea-scotland': Object.freeze({
    licensor: 'Scottish Environment Protection Agency',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.sepa.org.uk/environment/water/flooding/',
    attributionRequired: true,
    attributionText: SEPA_ATTRIBUTION,
    attributionUrl: SEPA_ATTRIBUTION_DOCUMENT,
    note:
      'The SEPA line is verbatim. The roughly forty third-party statements ' +
      'their attribution PDF also requires are discharged by linking, which ' +
      "is OGL v3's own \"including or linking to\" alternative.",
  }),
  'sepa-surface-water-scotland': Object.freeze({
    licensor: 'Scottish Environment Protection Agency',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.sepa.org.uk/environment/water/flooding/',
    attributionRequired: true,
    attributionText: SEPA_ATTRIBUTION,
    attributionUrl: SEPA_ATTRIBUTION_DOCUMENT,
    note:
      'Same licence, same statement and same linked document as the rivers ' +
      'and sea layer.',
  }),

  // --- Protected sites ----------------------------------------------------
  // SETTLED 10/09/2026. Natural England's service items name OGL v3 by URL in
  // `licenseInfo` and state "There are no public access constraints to this
  // data". One credit covers all three designations.
  //
  // The `licensor` here used to read "Natural England; Environment Agency",
  // taken from legend-metadata's prose. The services are Natural England's and
  // the credit names only them, so the EA half was wrong and is dropped.
  'sssi-england': Object.freeze({
    licensor: 'Natural England',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attributionRequired: true,
    attributionText: NATURAL_ENGLAND_ATTRIBUTION,
    note:
      "Verbatim from the service root's `copyrightText`. The SSSI service " +
      'description also states sites notified under the 1949 Act only, and ' +
      '"proposed" sites, are excluded. That is a data-coverage caveat for ' +
      'the layer copy rather than an attribution matter.',
  }),
  'sac-england': Object.freeze({
    licensor: 'Natural England',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attributionRequired: true,
    attributionText: NATURAL_ENGLAND_ATTRIBUTION,
    note:
      'Same credit as SSSI. The service states the data has the status ' +
      '"Candidate" and excludes "Possible" sites, which is worth checking ' +
      'against what the layer claims, and is layer copy rather than a credit.',
  }),
  'spa-england': Object.freeze({
    licensor: 'Natural England',
    licence: 'Open Government Licence v3.0',
    reference: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
    attributionRequired: true,
    attributionText: NATURAL_ENGLAND_ATTRIBUTION,
    note:
      'Same credit as SSSI. The service states the data has the status ' +
      '"Classified" and excludes "Potential" sites.',
  }),
  // SETTLED 10/09/2026, and it answers the AC0000849444 question. OGL v3 is
  // declared per dataset in the DataMapWales catalogue records, the service
  // reports AccessConstraints: NONE, and NRW's own position matches.
  //
  // AC0000849444 is NRW's OWN Ordnance Survey licence number, sitting inside
  // the credit NRW ask re-users to display. BBB does not need one of its own:
  // OS's guidance is that the member inserts THEIR licence number, and BBB is
  // not a PSGA member. The June 2026 handover recorded this as a string the app
  // had and did not render; the app never had it, and it is here now.
  'nrw-sssi-wales': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_DESIGNATIONS_ATTRIBUTION,
    note:
      'Verbatim from the DataMapWales catalogue record, found identically on ' +
      'all three Welsh designation datasets. The capitalisation of "All ' +
      'rights Reserved" is the publisher\'s.',
  }),
  'nrw-sac-wales': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_DESIGNATIONS_ATTRIBUTION,
    note: 'Same specified credit as the Welsh SSSI dataset.',
  }),
  'nrw-spa-wales': Object.freeze({
    licensor: 'Natural Resources Wales',
    licence: 'Open Government Licence v3.0',
    reference: 'https://datamap.gov.wales/',
    attributionRequired: true,
    attributionText: NRW_DESIGNATIONS_ATTRIBUTION,
    note: 'Same specified credit as the Welsh SSSI dataset.',
  }),
  // SETTLED 10/09/2026. NatureScot was the source nobody had ever checked, and
  // it turned out to be the cleanest on the board. The WMS this app calls
  // declares `Fees: NONE` and `AccessConstraints: NONE`, their catalogue items
  // name OGL v3, and their own FAQ expressly permits distribution
  // "commercially and non-commercially".
  //
  // THREE credits, not one, and they are not collapsed. NatureScot specify a
  // different string per designation and the three differ in how the body names
  // itself. Composing one line out of them would be writing wording rather than
  // quoting it. The stray space before the comma in the SSSI string is what the
  // publisher published.
  //
  // The Ordnance Survey element in each is a REAL SECOND OBLIGATION and is not
  // discharged by ONS_OS_BOUNDARY_ATTRIBUTION: that covers a different OS
  // derivation and carries a different year. The 2023 here is baked into
  // NatureScot's own wording, so it is quoted as published and not rolled
  // forward.
  'naturescot-designated-scotland': Object.freeze({
    licensor: 'NatureScot',
    licence: 'Open Government Licence v3.0',
    reference:
      'https://www.nature.scot/professional-advice/protected-areas-and-species/protected-areas',
    attributionRequired: true,
    attributionText: Object.freeze([
      '\u00a9 SNH , Contains Ordnance Survey data \u00a9 Crown copyright and database right (2023)',
      '\u00a9 NatureScot, Contains Ordnance Survey data \u00a9 Crown copyright and database right (2023)',
      '\u00a9 NatureScot (Scottish Natural Heritage), Contains Ordnance Survey data \u00a9 Crown copyright and database right (2023)',
    ]),
    note:
      "Verbatim from the `accessInformation` field of each of NatureScot's " +
      'three catalogue items, corroborated by `copyrightText` on each ' +
      'FeatureServer. The shipped map layer is one fused WMS carrying SSSI, ' +
      'SAC and SPA, which is why one source id owes three credits.',
  }),

  // --- Nitrate and water --------------------------------------------------
  // Seven lines, not one, and that is the publisher's own required credit. The
  // NVZ layer is derived from BGS, Cranfield, OS, Defra, CEH and the water
  // companies, and the Environment Agency require all of them named.
  'nvz-2021-england': Object.freeze({
    licensor: 'Environment Agency; Defra',
    licence: 'Open Government Licence',
    reference: 'https://www.gov.uk/government/collections/nitrate-vulnerable-zones',
    attributionRequired: true,
    attributionText: Object.freeze([
      '\u00a9 Environment Agency copyright and/or database right 2023. All rights reserved.',
      'Derived in part from geological mapping data provided by the British Geological Survey \u00a9 NERC.',
      'Derived in part from data provided by the National Soils Research Institute \u00a9 Cranfield University.',
      'Contains Ordnance Survey data \u00a9 Crown copyright and database rights 2016.',
      'Derived in part from data provided by the Department for the Environment, Farming and Rural Affairs \u00a9 Crown 2016 copyright Defra.',
      'Derived in part from data provided by the Centre for Ecology and Hydrology \u00a9 NERC.',
      'Derived in part from data provided by UK Water Companies.',
    ]),
    note:
      "Verbatim, all seven lines, from the dataset's own \"Attribution " +
      'statement:" block on data.gov.uk. The service\'s own licenseInfo ' +
      'carries the same list with the year dropped from the first line; the ' +
      'dated version is used because the dataset record is the licence source.',
  }),
  'drwpa-england': Object.freeze({
    licensor: 'Environment Agency',
    licence: 'Open Government Licence',
    reference:
      'https://www.data.gov.uk/dataset/6ac22521-2e77-4dc8-ba90-6bb55d2ea3b8/drinking-water-safeguard-zones-surface-water',
    attributionRequired: true,
    attributionText:
      '\u00a9 Environment Agency copyright and/or database right 2022. All rights reserved.',
    note:
      'Verbatim from the dataset abstract. Separately, the abstract carries a ' +
      'caveat worth putting in the layer\'s farmer-facing wording rather than ' +
      'in the credits: "Please note the status of these Safeguard Zones are ' +
      'continuously under review and details may change or become out of ' +
      'date once published." That is a layer copy call, not an attribution.',
  }),
  // ONE PLACEHOLDER AWAY FROM SETTLED, 10/09/2026. OGL v3 is declared against
  // this exact REST URL, with "No limitations to public access". The required
  // statement is published too, and it ends "(insert year)".
  //
  // That literal placeholder is the publisher's, and filling it is a judgement
  // about which year applies to the data we consume. Inventing one is exactly
  // what this register must not do, so the entry stays outstanding on a
  // one-question blocker rather than shipping a guess. The wording, verbatim
  // and unresolved, is: "The following attribution statement must be used to
  // acknowledge the source of the information: Copyright Scottish Government,
  // contains Ordnance Survey data (c) Crown copyright and database right
  // (insert year)".
  'nvz-scotland': outstanding(
    'Scottish Government',
    'Licence settled: OGL v3 declared against this exact REST URL, "No limitations to public access". The required statement is published but ends with the literal placeholder "(insert year)". One question, not a research job: which year. Then it ships.',
    'https://www.gov.scot/publications/nitrate-vulnerable-zones-guidance-for-farmers/'
  ),
});

/**
 * The credits to show for whatever is on screen.
 *
 * Always includes the always-required credits, then adds those of every active
 * source with a known required wording. A source whose wording is unknown
 * contributes NOTHING rather than a placeholder: an invented credit is worse
 * than a missing one, because it looks discharged.
 *
 * One source can owe several credits: NatureScot specify a different string per
 * designation and the shipped layer is one fused WMS of all three.
 *
 * Deduplicated by text, because several sources share one publisher and one
 * form of words.
 *
 * @param {Array<string>} activeSourceIds ids of the sources currently rendered
 * @returns {Array<{text: string, url: string|null}>} credits in a stable order.
 *   `url` is set only where the publisher's required statement is a document
 *   rather than a line, which OGL v3 lets us satisfy by linking.
 */
export function mapCredits(activeSourceIds = []) {
  const credits = [];
  const seen = new Set();

  const add = (text, url = null) => {
    if (!text || seen.has(text)) return;
    seen.add(text);
    credits.push({ text, url });
  };

  const addEntry = (entry) => {
    if (!entry) return;
    const { attributionText, attributionUrl = null } = entry;
    if (Array.isArray(attributionText)) {
      for (const text of attributionText) add(text, attributionUrl);
    } else {
      add(attributionText, attributionUrl);
    }
  };

  for (const entry of ALWAYS_REQUIRED) addEntry(entry);
  for (const id of activeSourceIds) addEntry(SOURCE_ATTRIBUTIONS[id]);

  return credits;
}

/**
 * Sources that owe an attribution nobody has the wording for.
 *
 * This is the production blocker in list form. Every entry is somebody reading
 * a publisher's terms, not a code change.
 *
 * @returns {Array<{id: string, licensor: string, note: string}>}
 */
export function outstandingAttributions() {
  return Object.entries(SOURCE_ATTRIBUTIONS)
    .filter(([, entry]) => entry.attributionRequired && entry.attributionText == null)
    .map(([id, entry]) => ({ id, licensor: entry.licensor, note: entry.note }));
}
